import React, { useMemo, useState } from 'react';
import {
  Eye, Download, Pencil, Trash2, Folder, FolderOpen, ChevronRight, AlertTriangle, Clock, Loader2, Lock, LockOpen, Stamp,
} from 'lucide-react';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { DateiIcon } from './DateiIcon';
import { anker } from '../../tutorial/anker';
import { ProjektDokument } from './types';
import {
  DOKUMENTE_JE_SEITE, ERZEUGT_HINWEIS, ERZEUGT_KENNZEICHEN, OrdnerKnoten, ablaufStatus, ablaufWarnungen,
  baueOrdnerBaum, dokumenteImAst, erzeugtVon, erzeugteLoeschSperre, formatBytes, kategorieLabel, modulLabel,
  ordnerKette, ordnerPfade,
} from './dokumenteLogic';

interface Props {
  dokumente: ProjektDokument[];
  /** ISO-Tag (YYYY-MM-DD) fuer die Ablaufbewertung — hereingereicht, nicht selbst geholt. */
  heute: string;
  isReadOnly?: boolean;
  /** Nur-Ansicht-Hinweis fuer die gesperrten Schaltflaechen. */
  readOnlyHinweis?: string;
  /** Kategorie mit anzeigen? In der Ablage steuert der Reiter sie schon, im Modulpanel nicht. */
  zeigeKategorie?: boolean;
  onAnsehen: (d: ProjektDokument) => Promise<void>;
  onHerunterladen: (d: ProjektDokument) => Promise<void>;
  onBearbeiten?: (d: ProjektDokument) => void;
  onLoeschen?: (d: ProjektDokument) => Promise<void>;
  /**
   * Darf der Betrachter das Admin-Schloss setzen (Admin oder Recht
   * „„Nur Admin“-Dokumente“, darfAdminSchloss)? Steuert NUR die Anzeige des
   * Schloss-Knopfes.
   *
   * Der Riegel selbst sitzt in der Datenbank (Migration Nr. 38, fuer das Recht
   * Nr. 154) — wer es nicht darf, bekommt ein gesperrtes Dokument ohnehin nie
   * zu sehen, und sperren koennte er auch dann nichts, wenn dieser Knopf hier
   * stuende.
   */
  darfSchloss?: boolean;
  /** Schloss setzen oder aufheben. Fehlt der Rueckruf, gibt es keinen Knopf. */
  onSperreUmschalten?: (d: ProjektDokument) => Promise<void>;
  /**
   * Darf der Betrachter von der App abgelegte Fassungen loeschen —
   * Verwaltung oder Uebungsprojekt (`darfErzeugteLoeschen`)? Vorgabe `false`:
   * der Loeschknopf solcher Zeilen ist dann ausgegraut und erklaert sich im
   * Tooltip. Der Riegel ist die Policy (Nr. 160/161); hier wird nur
   * verhindert, dass ein Klick erst die Datei entfernt und dann an der Zeile
   * scheitert.
   */
  darfErzeugteLoeschen?: boolean;
  /**
   * Kennung des aktiven Such-/Ablauffilters, leer = kein Filter. Mit Filter
   * starten alle Ordner offen — sonst laegen die Treffer in zugeklappten
   * Ordnern und die Suche saehe aus, als faende sie nichts. Jeder Wechsel
   * setzt Auf-/Zuklappen und die Seitenbegrenzung zurueck.
   */
  filterSchluessel?: string;
  /**
   * Ordner, die aufgehen sollen — nach dem Ablegen oder Verschieben eines
   * Dokuments. Jede NEUE Liste (neue Identitaet) oeffnet deren ganzen Weg;
   * ohne das verschwaende eine frisch abgelegte Datei in einem zugeklappten
   * Ordner, und es saehe aus, als sei der Upload gescheitert.
   */
  aufklappen?: string[];
}

/**
 * Die Ordner einer Liste samt aller Elternordner auf „offen" setzen.
 *
 * Map statt Objekt, auch fuer die Seitengrenzen: die Schluessel sind frei
 * eingetippte Ordnernamen, und ein Ordner „constructor" oder „__proto__"
 * traefe in einem Objekt geerbte Werte statt keine.
 */
const oeffne = (stand: Map<string, boolean>, ordner: string[] | undefined): Map<string, boolean> => {
  if (!ordner?.length) return stand;
  const neu = new Map(stand);
  for (const o of ordner) for (const p of ordnerKette(o)) neu.set(p, true);
  return neu;
};

/**
 * Die Dateiliste — als Ordnerbaum, ordnerlose Dokumente zuerst.
 *
 * Reine Anzeige: Filtern und Sortieren sind schon geschehen (pure Funktionen
 * in dokumenteLogic.ts), hier wird nur noch der Baum gebaut und gezeichnet.
 * Die Aktionen kommen als Zusagen von aussen, damit dieselbe Liste in der
 * Ablage und in der eingebetteten Modulleiste stehen kann.
 *
 * Ordner lassen sich zuklappen. In einer kleinen Ablage (bis
 * DOKUMENTE_JE_SEITE Dokumente) und waehrend einer Suche starten sie offen,
 * in einer grossen zugeklappt — dann sieht man zuerst, WAS es gibt, und
 * oeffnet gezielt. Jeder Ordner zeigt hoechstens DOKUMENTE_JE_SEITE Zeilen,
 * der Rest kommt auf Klick.
 */
export const DokumenteListe: React.FC<Props> = ({
  dokumente, heute, isReadOnly = false, readOnlyHinweis, zeigeKategorie = false,
  darfSchloss = false, onAnsehen, onHerunterladen, onBearbeiten, onLoeschen, onSperreUmschalten,
  darfErzeugteLoeschen = false, filterSchluessel = '', aufklappen,
}) => {
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Was der Nutzer selbst auf- oder zugeklappt hat; alles andere folgt `standardOffen`. */
  const [umgeschaltet, setUmgeschaltet] = useState<Map<string, boolean>>(() => oeffne(new Map(), aufklappen));
  /** Wie viele Zeilen je Ordner gerade zu sehen sind; fehlt der Eintrag, gilt DOKUMENTE_JE_SEITE. */
  const [angezeigt, setAngezeigt] = useState<Map<string, number>>(() => new Map());

  // Zuruecksetzen beim Wechsel der Props — waehrend des Renderns statt im
  // Effekt, damit kein Bild mit dem alten Stand dazwischen aufblitzt.
  const [filterStand, setFilterStand] = useState(filterSchluessel);
  if (filterStand !== filterSchluessel) {
    setFilterStand(filterSchluessel);
    setUmgeschaltet(new Map());
    setAngezeigt(new Map());
  }
  const [aufklappStand, setAufklappStand] = useState(aufklappen);
  if (aufklappStand !== aufklappen) {
    setAufklappStand(aufklappen);
    setUmgeschaltet(u => oeffne(u, aufklappen));
  }

  const baum = useMemo(() => baueOrdnerBaum(dokumente), [dokumente]);
  const standardOffen = Boolean(filterSchluessel) || dokumente.length <= DOKUMENTE_JE_SEITE;
  const istOffen = (pfad: string) => umgeschaltet.get(pfad) ?? standardOffen;
  const alleSetzen = (offen: boolean) =>
    setUmgeschaltet(new Map(ordnerPfade(baum).map(p => [p, offen])));

  const fuehreAus = async (id: string, was: () => Promise<void>) => {
    setBeschaeftigt(id);
    setFehler(null);
    try {
      await was();
    } catch (e) {
      setFehler(rechtFehlerOder(e));
    } finally {
      setBeschaeftigt(null);
    }
  };

  // 44 px hoch UND breit: die Kachel ist ausdruecklich auch auf dem Handy
  // sichtbar (ProjectModuleGrid), und vier Knoepfe nebeneinander in einer Zeile
  // sind mit dem Daumen sonst nicht sicher zu treffen — der Loeschknopf steht
  // direkt neben dem Herunterladen.
  const aktion = 'min-h-[44px] min-w-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold '
    + 'rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  if (!dokumente.length) {
    return (
      <div className="py-16 text-center text-slate-400 bg-white rounded-2xl border-2 border-dashed border-slate-200">
        <Folder className="w-10 h-10 mx-auto mb-3 opacity-20" />
        <p className="text-sm font-semibold text-slate-500">Hier liegt noch nichts.</p>
        <p className="text-xs mt-1">Pläne, Verträge, Genehmigungen und Nachweise gehören in diese Ablage.</p>
      </div>
    );
  }

  // Die Tour braucht genau EINEN Anker — auf der ersten Zeile, die wirklich
  // gezeichnet wird. Mit zugeklappten Ordnern ist das nicht mehr zwingend die
  // erste Zeile der Eingabe; deshalb vergibt ihn das Zeichnen selbst.
  let ankerFrei = true;

  const zeile = (d: ProjektDokument) => {
    const status = ablaufStatus(d.gueltig_bis, heute);
    const laeuft = beschaeftigt === d.id;
    const erste = ankerFrei;
    ankerFrei = false;
    /** Welches Modul die Zeile abgelegt hat — `null` fuer eine hochgeladene Datei. */
    const erzeugt = erzeugtVon(d);
    /** Warum Loeschen hier nicht geht — `null`, wenn es geht. */
    const loeschSperre = erzeugteLoeschSperre(d, darfErzeugteLoeschen);
    return (
      <div
        key={d.id}
        {...anker('dokumente.zeile', erste)}
        className={`bg-white p-3 rounded-xl border shadow-sm flex items-start gap-3 transition-colors ${
          status === 'abgelaufen' ? 'border-rose-200 hover:border-rose-300'
            : status === 'laeuft_ab' ? 'border-amber-200 hover:border-amber-300'
              : 'border-slate-200 hover:border-blue-300'
        }`}
      >
        {/* Das Sinnbild zeigt die DATEIART, nicht den Ablauf: den
            tragen der Rahmen der Zeile und die Kennzeichen am Titel.
            Wer die Ablage ueberfliegt, sucht zuerst „das PDF" und
            erst danach, ob es noch gilt. */}
        <DateiIcon dateiname={d.dateiname} mime={d.mime_typ} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-800 text-sm break-words">{d.titel}</span>
            {d.stand && (
              <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                {d.stand}
              </span>
            )}
            {/* Das Schloss ist die auffaelligste Eigenschaft der
                Zeile und steht deshalb direkt am Titel: wer eine
                gesperrte Unterlage vor sich hat, muss das sehen,
                bevor er sie weitergibt. Wer das Schloss nicht setzen
                darf, bekommt diese Zeile ohnehin nie zu Gesicht. */}
            {d.nur_admin && (
              <span
                title="Admin-Schloss: dieses Dokument ist nur für die Verwaltung sichtbar."
                className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5"
              >
                <Lock className="w-3 h-3" /> nur Verwaltung
              </span>
            )}
            {/* Eine von der App abgelegte Fassung ist ein Nachweis und
                keine hochgeladene Datei: das soll man sehen, bevor man
                sie bearbeitet oder zu loeschen versucht. */}
            {erzeugt && (
              <span
                title={ERZEUGT_HINWEIS}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
              >
                <Stamp className="w-3 h-3" /> {ERZEUGT_KENNZEICHEN} · {modulLabel(erzeugt)}
              </span>
            )}
            {status === 'abgelaufen' && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                <AlertTriangle className="w-3 h-3" /> abgelaufen
              </span>
            )}
            {status === 'laeuft_ab' && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <Clock className="w-3 h-3" /> läuft ab
              </span>
            )}
          </div>

          {d.beschreibung && <p className="text-xs text-slate-500 mt-1">{d.beschreibung}</p>}

          <p className="text-[11px] text-slate-400 mt-1 break-words">
            {zeigeKategorie && <>{kategorieLabel(d.kategorie)} · </>}
            {d.dateiname} · {formatBytes(d.groesse_bytes)}
            {d.gueltig_bis && <> · gültig bis {d.gueltig_bis.slice(0, 10).split('-').reverse().join('.')}</>}
            {d.hochgeladen_von_name && <> · {d.hochgeladen_von_name}</>}
          </p>

          {(d.tags.length > 0 || d.module.length > 0) && (
            <div className="flex flex-wrap gap-1 mt-2">
              {d.module.map(m => (
                <span key={m} className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">
                  {modulLabel(m)}
                </span>
              ))}
              {d.tags.map(t => (
                <span key={t} className="text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => fuehreAus(d.id, () => onAnsehen(d))} disabled={laeuft}
            className={`${aktion} text-slate-600 hover:bg-slate-100`} title="Ansehen"
          >
            {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => fuehreAus(d.id, () => onHerunterladen(d))} disabled={laeuft}
            className={`${aktion} text-slate-600 hover:bg-slate-100`} title="Herunterladen"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          {onBearbeiten && (
            <button
              {...anker('dokumente.bearbeiten', erste)}
              onClick={() => onBearbeiten(d)} disabled={isReadOnly || laeuft}
              className={`${aktion} text-slate-600 hover:bg-slate-100`}
              title={isReadOnly ? readOnlyHinweis : 'Angaben bearbeiten'}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {darfSchloss && onSperreUmschalten && (
            <button
              onClick={() => fuehreAus(d.id, () => onSperreUmschalten(d))}
              disabled={isReadOnly || laeuft}
              className={`${aktion} ${d.nur_admin ? 'text-indigo-600 hover:bg-indigo-50' : 'text-slate-600 hover:bg-slate-100'}`}
              title={isReadOnly ? readOnlyHinweis
                : d.nur_admin ? 'Admin-Schloss aufheben — danach sehen es alle Projektbeteiligten'
                  : 'Admin-Schloss setzen — danach sieht es nur die Verwaltung'}
            >
              {d.nur_admin ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
            </button>
          )}
          {onLoeschen && (
            <button
              onClick={() => {
                if (loeschSperre) return;
                const frage = erzeugt
                  ? `„${d.titel}" ist eine von der App abgelegte Fassung (Nachweis). Endgültig aus der Ablage löschen? Die Datei wird dabei gelöscht.`
                  : `„${d.titel}" endgültig aus der Ablage löschen? Die Datei wird dabei gelöscht.`;
                if (!window.confirm(frage)) return;
                void fuehreAus(d.id, () => onLoeschen(d));
              }}
              disabled={isReadOnly || laeuft || loeschSperre !== null}
              aria-disabled={loeschSperre !== null || undefined}
              className={`${aktion} text-slate-400 hover:text-rose-600 hover:bg-rose-50`}
              title={isReadOnly ? readOnlyHinweis : loeschSperre ?? 'Löschen'}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /** Die Dokumente direkt in einem Ordner — hoechstens so viele wie freigegeben, der Rest auf Klick. */
  const dateien = (k: OrdnerKnoten) => {
    if (!k.dokumente.length) return null;
    const grenze = angezeigt.get(k.pfad) ?? DOKUMENTE_JE_SEITE;
    const zeigen = k.dokumente.slice(0, grenze);
    const rest = k.dokumente.length - zeigen.length;
    const mehr = 'min-h-[36px] px-3 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors';
    return (
      <div className="space-y-2">
        {zeigen.map(zeile)}
        {rest > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
            <span className="text-[11px] text-slate-400">{zeigen.length} von {k.dokumente.length} angezeigt</span>
            <button
              type="button" className={mehr}
              onClick={() => setAngezeigt(a => new Map(a).set(k.pfad, grenze + DOKUMENTE_JE_SEITE))}
            >
              Weitere {Math.min(rest, DOKUMENTE_JE_SEITE)} anzeigen
            </button>
            {rest > DOKUMENTE_JE_SEITE && (
              <button
                type="button" className={mehr}
                onClick={() => setAngezeigt(a => new Map(a).set(k.pfad, k.dokumente.length))}
              >
                Alle {k.dokumente.length} anzeigen
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const ordner = (k: OrdnerKnoten): React.ReactNode => {
    const offen = istOffen(k.pfad);
    // Ein zugeklappter Ordner versteckt auch seine Warnungen — deshalb tragen
    // abgelaufene und bald ablaufende Nachweise ihre Zahl an den Ordnerkopf.
    const warnung = offen ? null : ablaufWarnungen(dokumenteImAst(k), heute);
    return (
      <div key={k.pfad}>
        <button
          type="button"
          onClick={() => setUmgeschaltet(u => new Map(u).set(k.pfad, !offen))}
          aria-expanded={offen}
          title={offen ? 'Ordner zuklappen' : 'Ordner aufklappen'}
          className="w-full min-h-[44px] flex items-center gap-2 px-2 rounded-xl text-left bg-slate-100/70 hover:bg-slate-200/70 transition-colors"
        >
          <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${offen ? 'rotate-90' : ''}`} />
          {offen
            ? <FolderOpen className="w-4 h-4 text-slate-500 shrink-0" />
            : <Folder className="w-4 h-4 text-slate-500 shrink-0" />}
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wide break-words min-w-0">{k.name}</span>
          <span className="text-[11px] text-slate-400 shrink-0">({k.gesamt})</span>
          {warnung && warnung.abgelaufen.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 shrink-0">
              <AlertTriangle className="w-3 h-3" /> {warnung.abgelaufen.length} abgelaufen
            </span>
          )}
          {warnung && warnung.laufenAb.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">
              <Clock className="w-3 h-3" /> {warnung.laufenAb.length} läuft ab
            </span>
          )}
        </button>
        {offen && (
          <div className="ml-4 pl-3 border-l-2 border-slate-200 mt-2 space-y-3">
            {dateien(k)}
            {k.unterordner.map(ordner)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {fehler && (
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 whitespace-pre-line">{fehler}</div>
      )}

      {baum.unterordner.length > 0 && (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button" onClick={() => alleSetzen(true)}
            className="min-h-[36px] px-3 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
          >
            Alle aufklappen
          </button>
          <button
            type="button" onClick={() => alleSetzen(false)}
            className="min-h-[36px] px-3 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
          >
            Alle zuklappen
          </button>
        </div>
      )}

      {dateien(baum)}
      {baum.unterordner.map(ordner)}
    </div>
  );
};
